import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import cx from 'classnames';
import { createSelector } from 'reselect';

import { Urls, CampaignTypes, ModalTypes, ReportTypes } from 'constants/constants';
import { formatHours as fm, formatNumber as fn, formatPercent as fp, formatHours, pluralize } from 'utils/utils';

import UIState from 'store/UIState';
import Campaign, { selectCampaign } from 'store/models/Campaign';
import Brand, { selectBrand } from 'store/models/Brand';
import { selectScenesForCampaign } from 'store/models/Scene';
import { selectLinksForCampaign } from 'store/models/Link';
import { selectStreamsForCampaign, isStreamLive } from 'store/models/Stream';
import { selectSessionsForCampaign } from 'store/models/Session';
import Channel, { selectChannels, selectChannelsByIds } from 'store/models/Channel';
import Game, { selectAllGames } from 'store/models/Game';
import RouteState, { newTab } from 'store/RouteState';
import AppState, { selectDates } from 'store/AppState';
import Stats, { selectStatsByCampaign, selectCampaignStreamStats, selectCampaignSceneStats, selectCampaignLinkStats, selectCampaignGameStats } from 'store/models/Stats';
import { getAllTags, selectCurrentAccount } from 'store/models/Account';

import { ContentWide, MegaHeader, Section, SectionTitle } from 'components/Content';
import ScrollToTop from 'components/ScrollToTop';
import { StatBox, StatsBoxList } from 'components/Stats';
import { TagList, ChannelTag, ChannelGroupTag } from 'components/Tag';
import { MinusCircleIcon } from 'components/Svg';
import DateRange from 'components/DateRange';
import { RefreshIconButton, EditButton, ButtonListUniform, DownloadIconButton } from 'components/Button';
import StatusDot from 'components/StatusDot';
import { SceneStatList } from 'components/SceneItem';
import { StreamStatList } from 'components/StreamItem';
import { GameList } from 'components/GameItem';
import { LinkList } from 'components/LinkItem';
import { LoadingText } from 'components/Loading';




// ======================
// PARTIALS
// ======================
const LoadingListItem = ({...props}) => {

}

// ======================
// STORE
// ======================
const selector = createSelector(
  selectCurrentAccount,
  selectDates,
  (state, {campaignId}) => selectCampaign(state, campaignId),
  (state, {campaignId}) => selectCampaign(state, campaignId) |> selectBrand(state, #?.brandId),
  (state, {campaignId}) => selectCampaign(state, campaignId) |> selectChannelIds(state, #),
  (state, {campaignId}) => selectStatsByCampaign(state, campaignId),
  selectCampaignStreamStats,
  selectCampaignSceneStats,
  selectCampaignLinkStats,
  selectCampaignGameStats,
  (state, {campaignId}) => selectScenesForCampaign(state, campaignId),
  (state, {campaignId}) => selectLinksForCampaign(state, campaignId),
  (state, {campaignId}) => selectStreamsForCampaign(state, campaignId),
  (state, {campaignId}) => selectSessionsForCampaign(state, campaignId),
  (state, props) => selectAllGames(state),
  (account, dates, campaign, brand, channels, stats, streamStats, sceneStats, linkStats, gameStats, scenes, links, streams, sessions, games) => {
    let activeScenes = campaign?.scenes |> _.map(#, 'sceneId') |> _.uniq;
    let activeLinks = campaign?.links |> _.map(#, 'linkId') |> _.uniq;
    games = games 
      |> _.pick(#, _.keys(gameStats)) 
      |> _.orderBy(#, g => gameStats[g._id]?.gameTimeMinutes ?? 0, 'desc');
    return { 
      account,
      dates,
      campaign,
      brand,
      channels,
      filteredChannels: _.filter(channels, c => _.find(campaign.filters, { channelId: c._id })),
      stats,
      streamStats, 
      sceneStats, 
      linkStats,
      gameStats,
      scenes, 
      activeScenes,
      links,
      games,
      activeLinks,
      streams: _.orderBy(streams, s => isStreamLive(s) ? 1000000000000000 : new Date(s?.created).valueOf(), 'desc'),
      sessions,
    }
  },
);

const selectChannelIds = (state, campaign) => campaign?.type === CampaignTypes.ALL
  ? selectChannels(state)
  : selectChannelsByIds(state, _.map(campaign?.channels, 'channelId'))


// ======================
// COMPONENT
// ====================== 
export const CampaignDetailsScreen = ({...props}) => {
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingScenes, setLoadingScenes] = useState(true);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [loadingStreams, setLoadingStreams] = useState(true);
  const [loadingGames, setLoadingGames] = useState(true);
  const dispatch = useDispatch();
  const { campaignId } = useParams();
  let { 
    account,
    dates, 
    campaign, 
    brand, 
    channels, 
    filteredChannels,
    stats, 
    streamStats, 
    sceneStats, 
    linkStats,
    gameStats,
    scenes, 
    activeScenes,
    links,
    activeLinks, 
    streams,
    sessions,
    games } = useSelector(state => selector(state, {campaignId}));
  const tags = getAllTags(account) |> _.keyBy(#, 'tagId');

  
  
  useEffect(() => {
    (async () => {
      setLoading(true);
      await dispatch(Campaign.actions.fetchCampaign(campaignId));
      await dispatch(Brand.actions.fetchBrands());
      await dispatch(Channel.actions.fetchChannels());
      setLoading(false);

      fetchStats();
    })();
  }, [])



  const fetchStats = async () => {
    let from = dates.from.valueOf();
    let to = dates.to.valueOf();
    
    setLoadingStats(true);
    setLoadingStreams(true);
    setLoadingLinks(true);
    setLoadingScenes(true);

    await dispatch(Stats.actions.fetchCampaignDetailStats(campaignId, from, to));
    setLoadingStats(false);

    await dispatch(Stats.actions.fetchCampaignStreamStats(campaignId, from, to));
    setLoadingStreams(false);
    
    await dispatch(Stats.actions.fetchCampaignLinkStats(campaignId, from, to));
    setLoadingLinks(false);
    
    await dispatch(Stats.actions.fetchCampaignSceneStats(campaignId, from, to));
    setLoadingScenes(false);

    await dispatch(Stats.actions.fetchCampaignGameStats(campaignId, from, to));
    setLoadingGames(false);
  }

  useEffect(() => {
    if(campaign) {
      (async () => {
        setLoading(true);
        await fetchStats();
        setLoading(false);
      })();
    }
  }, [dates.from.valueOf(), dates.to.valueOf()])

  const onRefreshStats = async () => {
    setLoading(true);
    await fetchStats();
    setLoading(false);
  }
  const onExportView = () => dispatch(UIState.actions.showModal(ModalTypes.EXPORT, { report: ReportTypes.CAMPAIGN, brandId: brand._id, campaignId: campaign._id }));
  const onEditCampaign = () => dispatch(RouteState.actions.navigate(Urls.CAMPAIGN_EDIT_ROUTE, { brandId: brand._id, campaignId }))
  const onViewChannel = (channelId) => newTab(Urls.CHANNEL_DETAILS_ROUTE, { channelId });

  return <>
    <ScrollToTop />
    <MegaHeader className="campaign-details-head">
      <div className="campaign-info">
        <h3>{ brand?.name }</h3>
        <h1>{ campaign?.name }</h1>
        <div className="campaign-status">
          <span>Campaign is <StatusDot status={campaign?.status} /> <strong className={campaign?.status}>{campaign?.status}</strong></span>
        </div>
        <div className="info-item-list">
          <div className="info-item targets">
            { campaign?.type === CampaignTypes.ALL &&
              <div className="info-label">
                <span>Targeting <strong>{ _.size(channels) > 1 ? 'All' : '' } { _.size(channels) } { pluralize('Channel', _.size(channels)) }</strong></span>
                {_.size(filteredChannels) > 0 &&
                <span className="filtered-channels">, except these <strong>{_.size(filteredChannels)}</strong></span>}
              </div>}
            { campaign?.type === CampaignTypes.CHANNELS && <>
              <div className="info-label">
                Targeting <strong>{ _.size(channels) } { pluralize('Channel', _.size(channels)) }</strong>
              </div>
              <TagList>
                { _.map(channels, channel => 
                  <ChannelTag 
                    key={channel._id} 
                    channel={channel} 
                    tagId={channel._id}
                    onClick={onViewChannel} />) }
              </TagList>
            </>}
            { campaign?.type === CampaignTypes.TAGS && <>
              <div className="info-label">
                Targeting <strong>{ _.size(campaign?.tags) } { pluralize('Channel Group', _.size(campaign?.tags)) }</strong>
              </div>
              <TagList>
                { _.map(campaign?.tags, tagId => <ChannelGroupTag key={tagId} tag={tags?.[tagId]} />) }
              </TagList>
            </>}
            {_.size(filteredChannels) > 0 &&
            <TagList>
              { _.map(filteredChannels, channel => 
                <ChannelTag 
                  key={channel._id} 
                  channel={channel} 
                  tagId={channel._id}
                  icon={<MinusCircleIcon size="s" color="red" />}
                  onClick={onViewChannel} />) }
            </TagList>}
          </div>
        </div>
      </div>
      <ButtonListUniform>
        <EditButton color="light" onClick={onEditCampaign}>Edit Campaign</EditButton>
      </ButtonListUniform>
    </MegaHeader>
    <ContentWide className="campaign-details">
      
    <Section className="date-range-header">
          <div className="total-value">
            <div className="label">Total Media Value</div>
            <div className="value">
              { loading 
                ? <LoadingText />
                : `$${fn(stats?.totalMediaValue)}`}
            </div>
          </div>
          <div className="date-export">
            <DateRange disabled={loading} />
            <DownloadIconButton color="light" disabled={loading} onClick={onExportView} />
            <RefreshIconButton color="light" loading={loading} onClick={onRefreshStats} />
          </div>
        </Section>
        
        <Section>
          <SectionTitle>Slides</SectionTitle>
          <StatsBoxList rowCount={4} wrap={true}>
            <StatBox
              label="Slideshow Value"
              value={`$${fn(stats?.sceneMediaValue)}`}
              loading={loadingStats} />
            <StatBox
              label="Impressions"
              value={fn(stats?.sceneViews)}
              loading={loadingStats} />
            <StatBox
              label="Unique Slides"
              value={fn(stats?.sceneIds)}
              loading={loadingStats} />
          </StatsBoxList>
          <SceneStatList 
            scenes={scenes} 
            sceneStats={sceneStats} 
            activeScenes={activeScenes}
            loading={loadingScenes}
            loadingCount={2} />
        </Section>

        <Section size="l">
          <SectionTitle>Links & Chat</SectionTitle>
          <StatsBoxList rowCount={4} wrap={true}>
            <StatBox
              label="Click Value"
              value={`$${fn(stats?.clicksMediaValue)}`}
              loading={loadingStats} />
            <StatBox
              label="Clicks"
              value={fn(stats?.clicks)}
              loading={loadingStats} />
            <StatBox
              label="Link Views"
              value={fn(stats?.messageViews)}
              loading={loadingStats} />
            <StatBox
              label="CTR"
              value={fp(stats?.linkCtr, 1)}
              loading={loadingStats} />
          </StatsBoxList>
          <LinkList 
            links={links} 
            linkStats={linkStats} 
            activeLinks={activeLinks} 
            loading={loadingLinks}
            loadingCount={2} />
        </Section>

        <Section size="l">
          <SectionTitle>Streams</SectionTitle>
          <StatsBoxList rowCount={4} wrap={true}>
            <StatBox
              label="Peak Viewers"
              value={fn(stats?.streamMaxViewers)}
              loading={loadingStats} />
            <StatBox
              label="Avg. Viewers"
              value={fn(stats?.streamAvgViewers)}
              loading={loadingStats} />
            <StatBox
              label="Streams"
              value={`${fn(stats?.streamCount)}`}
              loading={loadingStats} />
            <StatBox
              label="Time Streaming"
              value={fm(stats?.streamMinutes)}
              loading={loadingStats} />
          </StatsBoxList>
          <StreamStatList 
            streams={streams} 
            stats={streamStats} 
            sessions={sessions}
            channels={channels}
            showChannel={true}
            loading={loadingStreams} 
            loadingCount={2}  />
        </Section>
        <Section size="l">
          <SectionTitle>Games</SectionTitle>
          <GameList games={games} stats={gameStats} loading={loadingGames} showName={false} showViewers={true} />
        </Section>
    </ContentWide>
  </>
}
export default CampaignDetailsScreen